<script setup lang="ts">
import { computed, ref } from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VSplitter from '@components/splitter/VSplitter/VSplitter.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_PAYMENT_SYSTEMS } from '@modules/payment-systems/constants';
import { watchImmediate } from '@vueuse/core';

import type {
  AdminPaymentCountryGroupsFormConfigOption,
  IAdminPaymentCountryGroupsFormConfigItemEmits,
  IAdminPaymentCountryGroupsFormConfigItemProps,
  IAdminSettingsPaymentCountryGroupItemValidationData,
  IAdminSettingsPaymentCountryGroupItemValidationDataItem,
} from '../../types';
import {
  TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS,
} from '../../constants';

const props = defineProps<IAdminPaymentCountryGroupsFormConfigItemProps>();
const emit = defineEmits<IAdminPaymentCountryGroupsFormConfigItemEmits>();

const { getTranslation } = useI18n();

const validationData = ref<IAdminSettingsPaymentCountryGroupItemValidationData>();

const hasSelection = (item: IAdminSettingsPaymentCountryGroupItemValidationDataItem): boolean => (item.range.min !== null || item.range.max !== null || item.presence);

const fieldOptions = computed<AdminPaymentCountryGroupsFormConfigOption[]>(() => {
  if (props.data) {
    return Object.keys(props.data).sort().map((a) => ({
      value: a,
      label: getTranslation(TRANSLATION_GROUP_PAYMENT_SYSTEMS, `bank_transfer.${a}`).value,
      hasSelection: props.data ? hasSelection(props.data[a]) : false,
    }));
  }

  return [];
});

const selectedFieldNameModel = ref<string>();
const selectedFieldName = ref<string>();
const selectedFieldData = computed<
    IAdminSettingsPaymentCountryGroupItemValidationDataItem | undefined
>(() => (props.data && selectedFieldName.value ? props.data[selectedFieldName.value] : undefined));

watchImmediate(() => props.data, (newValue) => {
  validationData.value = newValue;
});

const formSchema = computed<FormSchema<IAdminSettingsPaymentCountryGroupItemValidationDataItem>>(() => ({
  presence: {
    type: EFormInputTypes.CHECKBOX,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'REQUIRED_FIELD').value,
    value: !!selectedFieldData.value?.presence,
  },
  range: {
    min: {
      type: EFormInputTypes.NUMBER,
      label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'MIN_LENGTH').value,
      value: selectedFieldData.value?.range.min || null,
      maxFractionDigits: 0,
    },
    max: {
      type: EFormInputTypes.NUMBER,
      label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'MAX_LENGTH').value,
      value: selectedFieldData.value?.range.max || null,
      maxFractionDigits: 0,
    },
  },
}));

function handleFieldSelection(value: string): void {
  if (value) {
    selectedFieldName.value = value;
  }
}

function handleFormChange<
  T extends IAdminSettingsPaymentCountryGroupItemValidationDataItem,
  K extends FormFlattenKey<T>,
>(key: K, value: FormFlattenValue<T, K> | undefined, { values }: FormResult<T>): void {
  if (validationData.value && selectedFieldName.value) {
    const newData = { ...validationData.value };
    newData[selectedFieldName.value] = values;
    emit('change', newData);
    emit('blur', newData);
  }
}

function handleClearSelection(name: string): void {
  const newData = { ...validationData.value };
  newData[name] = {
    presence: false,
    range: {
      min: null,
      max: null,
    },
  };
  emit('change', newData);
  emit('blur', newData);
}
</script>

<template>
  <VSplitter v-data-qa-auto-id="'PaymentCountryGroupsFormConfig'"
    :splitter-root-pt="{
      root: { class: 'max-w-[800px]' },
    }"
    :left-prime-vue-props="{ size: 30, minSize: 20 }"
    :right-prime-vue-props="{ size: 70, minSize: 40 }"
    :right-prime-vue-pt="{
      root: { class: 'p-6' },
    }"
  >
    <template #left="{ openItem }">
      <PListbox
        v-model="selectedFieldNameModel"
        :options="fieldOptions"
        option-value="value"
        :pt="{
          option: {
            class: '!p-2 h-[40px]',
          },
          listContainer: {
            class: '!max-h-full',
          },
        }"
        class="w-full border-none"
        @update:model-value="handleFieldSelection"
      >
        <template #option="{ option }">
          <div
            class="relative flex w-full flex-row flex-nowrap items-center"
            @click="openItem(option.label)"
          >
            <div class="flex w-4 items-center">
              <div
                v-if="option.hasSelection"
                class="size-[4px] rounded-full bg-primary"
              />
            </div>
            <span class="grow truncate p-1">
              {{ option.label }}
            </span>
            <span class="w-8">
              <VIconButton
                v-if="option.hasSelection"
                icon="pi-times"
                size="small"
                @click="handleClearSelection(option.value)"
              />
            </span>
          </div>
        </template>
      </PListbox>
    </template>
    <template #right>
      <VForm
        v-if="selectedFieldName"
        :schema="formSchema"
        @change="handleFormChange"
      />
    </template>
  </VSplitter>
</template>
